// Per-email reply history — the thread of replies we've sent (or are sending)
// for one inbound email. Reads email_reply_sends (migration 0052) by
// source_email_id. Shared query key with SendReplyButton so React Query dedupes
// the fetch and a fresh send shows up in both places at once.

import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Loader2, AlertCircle, CornerUpRight } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { supabase } from '@/lib/supabase';

export type SentReplyRow = {
  id: string;
  status: string;
  to_email: string | null;
  subject: string | null;
  body: string | null;
  error: string | null;
  sent_at: string | null;
  created_at: string;
};

// Only the rows that represent an actual send attempt (not in-progress drafts).
const SEND_STATES = new Set(['send_requested', 'sending', 'sent', 'send_error']);

export function useSentReplies(sourceEmailId: string) {
  return useQuery<SentReplyRow[]>({
    queryKey: ['email_reply_sends', sourceEmailId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('email_reply_sends')
        .select('id, status, to_email, subject, body, error, sent_at, created_at')
        .eq('source_email_id', sourceEmailId)
        .is('deleted_at', null)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return ((data as SentReplyRow[]) ?? []).filter((r) => SEND_STATES.has(r.status));
    },
    // Poll while anything is mid-send so 'sending' flips to 'sent' live.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((r) => r.status === 'sending' || r.status === 'send_requested')
        ? 2000
        : false,
  });
}

/** The count of replies that actually left (status='sent'). */
export function sentReplyCount(rows: SentReplyRow[] | undefined): number {
  return (rows ?? []).filter((r) => r.status === 'sent').length;
}

/** Replies whose send FAILED and haven't been superseded — surfaced so the
 *  summary state (badge / footer / button) never contradicts a "Failed" row
 *  visible in the thread. */
export function failedReplyCount(rows: SentReplyRow[] | undefined): number {
  return (rows ?? []).filter((r) => r.status === 'send_error').length;
}

/**
 * Whether closing the compose dialog should soft-delete the (un-sent) draft
 * row. Pure so the close-state machine is unit-testable (regression guard for
 * the "stuck modal / stranded send" bugs). Never delete once a send has been
 * submitted (submittedSend) or the row is already in a send state.
 */
export function shouldSoftDeleteOnClose(
  status: string | undefined,
  submittedSend: boolean,
): boolean {
  if (submittedSend || !status) return false;
  return !['sent', 'sending', 'send_requested'].includes(status);
}

export function SentRepliesSection({
  sourceEmailId,
}: {
  sourceEmailId: string;
}): JSX.Element | null {
  const { data: replies } = useSentReplies(sourceEmailId);
  if (!replies || replies.length === 0) return null;

  return (
    <div className="mt-4 border-t border-border pt-3">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">
        Your replies ({replies.length})
      </p>
      <ol className="space-y-2">
        {replies.map((r) => (
          <li key={r.id} className="neo-inset px-3 py-2">
            <div className="flex items-center gap-2 flex-wrap">
              <CornerUpRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              <span className="text-xs text-foreground truncate">
                You → {r.to_email ?? 'recipient'}
              </span>
              <StatusPill status={r.status} />
              <span className="ml-auto text-[11px] font-mono text-muted-foreground">
                {formatDistanceToNow(new Date(r.sent_at ?? r.created_at), { addSuffix: true })}
              </span>
            </div>
            {r.subject && (
              <p className="mt-1 text-[13px] font-medium text-foreground truncate">{r.subject}</p>
            )}
            {r.body && (
              <pre className="mt-1 max-h-40 overflow-y-auto whitespace-pre-wrap break-words font-sans text-[13px] text-muted-foreground leading-relaxed">
                {r.body}
              </pre>
            )}
            {r.status === 'send_error' && r.error && (
              <p className="mt-1 text-[11px] text-alert">Send failed: {r.error}</p>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

function StatusPill({ status }: { status: string }): JSX.Element {
  if (status === 'sent') {
    return (
      <span className="neo-chip inline-flex items-center gap-1 rounded-full bg-sage/10 px-1.5 py-0.5 text-[10px] text-sage">
        <CheckCircle2 className="w-3 h-3" /> Sent
      </span>
    );
  }
  if (status === 'send_error') {
    return (
      <span className="neo-chip inline-flex items-center gap-1 rounded-full bg-alert/10 px-1.5 py-0.5 text-[10px] text-alert">
        <AlertCircle className="w-3 h-3" /> Failed
      </span>
    );
  }
  return (
    <span className="neo-chip inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] text-muted-foreground">
      <Loader2 className="w-3 h-3 animate-spin" /> Sending
    </span>
  );
}
